/**
 * Dead-letter identity-projection admin API (PB-4825).
 *
 * The Identity & directory page already renders `sync.deadLetterCount` from the
 * tenant-configuration read. These two calls turn that number into something an
 * administrator can act on: list WHO is affected, and requeue one delivery.
 *
 * Uses the shared `request()` from admin-users-api rather than a second fetch
 * helper — it already carries CSRF, credentials and the `{code, message}` error
 * envelope raised as `AdminApiError`, which is what `describeAdminError` keys
 * its copy on. Same arrangement as invites-api.
 */
import { request } from "./admin-users-api.js";

export interface ProjectionFailure {
  readonly syncId: string;
  /** Row identity and audit correlation. Never rendered — a UUID on screen
      tells an administrator nothing. */
  readonly userId: string;
  /** Both nullable: an anonymized profile carries neither. */
  readonly displayName: string | null;
  readonly email: string | null;
  readonly attemptCount: number;
  readonly failedAt: string;
  /** Plain-language cause, curated server-side from the outbox error code. */
  readonly cause: string;
}

export interface ProjectionFailurePage {
  readonly failures: readonly ProjectionFailure[];
  readonly nextCursor: string | null;
}

/* The endpoint's MAX_LIMIT (services/auth/src/admin/admin.controller.ts). It
   400s above this rather than clamping, so the client must not exceed it — the
   same constraint `MEMBER_PAGE_SIZE` exists for on the members list. Kept well
   below the maximum because a tenant with more than a page of failed
   projections has an outage, not a paging problem. */
export const PROJECTION_FAILURE_PAGE_SIZE = 25;

/** Tenant-scoped, for the reason spelled out on `adminMemberSessionsQueryKey`:
    without the tenant segment an in-place tenant handover would serve the
    previous tenant's rows from cache. The server fences every request on the
    acting tenant regardless, so this is a cache defect rather than an
    authorization hole — but it would still disclose who could not sign in
    somewhere else. */
export function projectionFailuresQueryKey(
  tenantId: string | null | undefined,
): readonly unknown[] {
  return ["auth", "admin", "projection-failures", tenantId ?? "none"] as const;
}

export async function listProjectionFailures(
  cursor?: string | null,
  signal?: AbortSignal,
): Promise<ProjectionFailurePage> {
  const params = new URLSearchParams({
    limit: String(PROJECTION_FAILURE_PAGE_SIZE),
  });
  if (cursor) params.set("cursor", cursor);
  const payload = await request(
    `/api/auth/admin/projection-outbox/failures?${params.toString()}`,
    signal ? { signal } : {},
  );
  /* Shape-checked rather than cast, for the reason directory-status-api spells
     out: nothing enforces "200 implies the documented body" at runtime, and this
     page's rows are consumed by `useProgressiveCursorList`, which spreads
     `page.items`. A 200 carrying `{}` — an ingress or a proxy answering for the
     service — would otherwise hand `undefined` to that spread and throw during
     RENDER, taking the whole Identity page down instead of showing this card's
     own error state. That is not hypothetical: it is what the admin console's
     malformed-200 case actually did before this guard.

     Structural, not a schema validator: it proves only what the caller
     dereferences. Field VALUES stay untrusted — `displayName`/`email` are
     rendered through a fallback, and `cause` is already server-curated copy. */
  if (!isProjectionFailurePage(payload)) {
    /* Specialised rather than the shared "a response we could not read"
       sentence, following notification-policy-api: this card sits on the same
       tab as the directory-status section, which raises the shared phrase for
       its own read. Two identical sentences on one panel leave the
       administrator unable to tell WHICH read failed. */
    throw new Error(
      "The server returned failed-projection rows we could not read.",
    );
  }
  return payload;
}

function isProjectionFailurePage(
  value: unknown,
): value is ProjectionFailurePage {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  const cursor = candidate["nextCursor"];
  return (
    Array.isArray(candidate["failures"]) &&
    candidate["failures"].every(isProjectionFailure) &&
    (cursor === null || typeof cursor === "string")
  );
}

/** Every field the contract declares required is checked, not only the ones
    whose absence would throw — the same standard as `isDirectoryStatus`. */
function isProjectionFailure(value: unknown): value is ProjectionFailure {
  if (typeof value !== "object" || value === null) return false;
  const row = value as Record<string, unknown>;
  return (
    typeof row["syncId"] === "string" &&
    typeof row["userId"] === "string" &&
    (row["displayName"] === null || typeof row["displayName"] === "string") &&
    (row["email"] === null || typeof row["email"] === "string") &&
    typeof row["attemptCount"] === "number" &&
    typeof row["failedAt"] === "string" &&
    typeof row["cause"] === "string"
  );
}

export async function requeueProjection(
  syncId: string,
): Promise<{ readonly syncId: string; readonly status: "pending" }> {
  return (await request(
    `/api/auth/admin/projection-outbox/${encodeURIComponent(syncId)}/requeue`,
    { method: "POST" },
  )) as { readonly syncId: string; readonly status: "pending" };
}
